import type { ProgressMap } from '@/hooks/useProgress';
import { calculateGamification } from '@/lib/progress/gamification';

interface Props {
  progress: ProgressMap;
  compact?: boolean;
}

export default function StudentLevelCard({ progress, compact = false }: Props) {
  const game = calculateGamification(progress);

  if (compact) {
    return (
      <div className="flex min-w-[220px] items-center gap-3 rounded-xl border border-orange-100 bg-orange-50 px-3 py-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-white">
          <i className={game.level.icon} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 text-[11px] font-extrabold">
            <span className="truncate text-slate-800">Nível {game.level.index} · {game.level.name}</span>
            <span className="shrink-0 text-orange-700">{game.xp} XP</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-orange-100">
            <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${game.levelPercent}%` }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-orange-100 bg-white shadow-sm">
      <div className="grid gap-5 p-5 sm:grid-cols-[1fr_auto] sm:items-center md:p-6">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-md shadow-orange-500/20">
              <i className={`${game.level.icon} text-xl`} />
            </span>
            <div>
              <div className="text-xs font-extrabold uppercase tracking-[0.12em] text-orange-600">Sua evolução</div>
              <h2 className="mt-0.5 font-heading text-xl font-extrabold text-slate-950">
                Nível {game.level.index} · {game.level.name}
              </h2>
            </div>
          </div>

          <div className="mt-5 h-2.5 overflow-hidden rounded-full bg-orange-100">
            <div className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all" style={{ width: `${game.levelPercent}%` }} />
          </div>
          <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs font-semibold text-slate-500">
            <span>{game.xp} XP acumulados</span>
            <span>{game.level.nextXp === null ? 'Nível máximo atual' : `Faltam ${game.xpToNext} XP para o próximo nível`}</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-emerald-50 px-3 py-3">
            <div className="font-heading text-xl font-black text-emerald-700">{game.totalCorrect}</div>
            <div className="text-[10px] font-bold text-slate-500">acertos</div>
          </div>
          <div className="rounded-xl bg-red-50 px-3 py-3">
            <div className="font-heading text-xl font-black text-red-600">{game.totalWrong}</div>
            <div className="text-[10px] font-bold text-slate-500">erros</div>
          </div>
          <div className="rounded-xl bg-orange-50 px-3 py-3">
            <div className="font-heading text-xl font-black text-orange-700">{game.masteredLessons}</div>
            <div className="text-[10px] font-bold text-slate-500">dominadas</div>
          </div>
        </div>
      </div>
      <div className="border-t border-orange-100 bg-[#fffaf5] px-5 py-3 text-xs text-slate-600 md:px-6">
        <strong className="text-slate-800">Como funciona:</strong> cada acerto soma 5 XP e cada aula dominada dá 120 XP de bônus.
        O domínio da etapa é outra medida e pode cair quando você erra.
      </div>
    </section>
  );
}
